import React from "react";
import { useSelector } from "react-redux";
import { useToast } from "../../../provider/ToastProvider";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForClasses } from "../../../utils/confighelpers";
import { FcModal } from "../schedule/ui";
import { apiResult } from "./memberUtils";

/** Invite members with a club code (new design). Same call as before: POST generateInviteCode {expiryInDays: 30}. */

const APP_LINK = "https://onelink.to/j9byv4";

export default function InviteCodeModal({ open, onClose }) {
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const club = userReduxState.userData?.myBusinessProfile?.businessName || "our club";
  const [code, setCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCode("");
    setError("");
    setLoading(true);
    (async () => {
      try {
        const res = await axiosInstance({ method: "post", url: `${getUrlForClasses("")}generateInviteCode`, data: { expiryInDays: 30 } });
        const [ok, data, msg] = apiResult(res);
        if (cancelled) return;
        if (ok && data?.inviteCode) setCode(data.inviteCode);
        else setError(msg || "Couldn't create an invite code. Please try again.");
      } catch (e) {
        if (!cancelled) setError("Couldn't reach the server. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const message = `Join ${club} on FitClub with invite code ${code}.\nDownload the app: ${APP_LINK}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      addSuccessToast("Invite message copied");
    } catch (e) {
      addErrorToast("Couldn't copy. Select the text and copy it yourself.");
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="INVITE CODE"
      title="Invite members with a code"
      subtitle={`They download FitClub, enter the code and join ${club}.`}
      primaryLabel="Share on WhatsApp"
      primaryDisabled={!code}
      onPrimary={() => window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener")}
      footerNote={
        <button type="button" onClick={copy} disabled={!code} className="rounded-xl px-1 text-sm font-extrabold text-indigo-bz hover:underline disabled:opacity-40">
          Copy message
        </button>
      }
    >
      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">{error}</div>
      ) : (
        <>
          <div className="rounded-3xl bg-[#F3E9FF] px-5 py-6 text-center">
            {loading ? (
              <div className="mx-auto h-10 w-48 animate-pulse rounded-xl bg-white/60" />
            ) : (
              <div className="font-mono text-3xl font-extrabold tracking-[0.18em] text-brown-coffee sm:text-4xl">{code}</div>
            )}
            <div className="mt-2 text-[13px] font-bold text-[#6A00BA]">Valid for 30 days · works for everyone you send it to</div>
          </div>
          <div className="whitespace-pre-line rounded-2xl border border-gray-200 bg-[#FBF9FC] px-4 py-3 text-sm text-gray-700">{loading ? "…" : message}</div>
        </>
      )}
    </FcModal>
  );
}
